/**
 * MOVING BETWEEN SCREENS: the decisions, as pure functions.
 *
 * Everything here answers a question that has one right answer and no React in it -
 * may this navigation animate, which way is it going, is this click ours - so all of
 * it is testable without a DOM, and `RouteTransition.tsx` is left holding only the
 * wiring.
 *
 * WHY THIS IS HAND-ROLLED AT ALL, since react-router 8.3 ships `viewTransition` on
 * `Link`, `NavLink` and `useNavigate`. That support lives in the DATA and FRAMEWORK
 * modes only - the installed package says so in its own types
 * (`@param {LinkProps.viewTransition} [modes: framework, data]`) and its code proves
 * it: this app mounts `<BrowserRouter>` with `<Routes>`, which is DECLARATIVE mode,
 * so `useNavigate` resolves to `useNavigateUnstable`, which calls
 * `navigator.push(path, options.state, options)` - and `createBrowserHistory`'s
 * `push(to, state)` takes two parameters and drops the third on the floor. There is
 * no `startViewTransition` anywhere on that path. `useViewTransitionState` is worse
 * than useless here: it throws unless a data-mode `RouterProvider` is above it.
 *
 * Moving the app to data mode would rewrite the route table and main.tsx, which is a
 * structural decision and not one a motion work package gets to make. So the API is
 * driven directly, and the router is left exactly as it is.
 */

/** Deeper into the hierarchy, or back out of it. */
export type NavDirection = 'push' | 'pop';

/**
 * The attribute the stylesheet keys on. It is written on `<html>` for the duration of
 * one transition and removed after, so `view-transition-name` exists only while
 * something is moving (see ui/motion.css).
 */
export const NAV_DIRECTION_DATASET_KEY = 'navDirection';

/**
 * MAY THIS NAVIGATION ANIMATE?
 *
 * Three gates, and a failure of any means today's instant behaviour rather than a
 * degraded animation. The API is not everywhere (Safari and Firefox arrived later
 * than Chromium, and an old install of any of them is a browser this product still
 * has to work in), and a person who asked for reduced motion asked for exactly this.
 *
 * Reduced motion is checked HERE rather than left to the collapsed duration tokens:
 * a 0.01ms view transition still snapshots the document, still composites two layers
 * and still has a frame in which either could be painted. Not starting it is the only
 * honest reading of the preference.
 *
 * THE DESKTOP CHANGES PAGE WITHOUT ONE (BR-PERF; handbook I6, "Keine Schiebe-Animation vom
 * Handy am Desktop.", and Part 5 N12: "No push slide on the desktop; a page change is a
 * crossfade or none. Apple publishes no Mac push."). Of the two, none: opening a client from
 * the list and going back to it are the most frequent things a person does here, and I3 adds
 * no motion to frequent interactions and lets nobody wait for one, while I10 does not animate
 * cached content, which a return to a list is. A crossfade would also still draw both pages
 * at once for its whole length, the overlap the owner saw (4 Oct measurement, section 1a),
 * and over a destination that commits its skeleton the content would swap in mid-fade. The
 * phone keeps its push and pop; its breakpoint is the layout's own (`--breakpoint-desktop`,
 * as `ui/useIsDesktop.ts` reads it).
 */
export function canAnimateNavigation(view: Window = window): boolean {
  if (typeof view.document.startViewTransition !== 'function') return false;
  if (typeof view.matchMedia !== 'function') return false;
  if (view.matchMedia(desktopQuery(view)).matches) return false;
  return !view.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The layout's desktop breakpoint, as `ui/useIsDesktop.ts` builds it from the token. */
function desktopQuery(view: Window): string {
  const token =
    typeof view.getComputedStyle === 'function'
      ? view
          .getComputedStyle(view.document.documentElement)
          .getPropertyValue('--breakpoint-desktop')
      : '';
  return token.trim().length > 0 ? `(min-width: ${token.trim()})` : '(min-width: 48rem)';
}

/**
 * The router's own position in the history stack, as it writes it.
 *
 * `createBrowserHistory` stores `{ usr, key, idx }` in `history.state` and keys its
 * own POP handling on `idx` (verified in the installed react-router 8.3.0,
 * `lib/router/history.js`). Reading the same number is what lets a BACK gesture pop
 * and a FORWARD gesture push, which no comparison of the two paths could tell apart:
 * a record and the related record beside it sit at the same depth.
 */
export function historyIndex(state: unknown): number | null {
  if (typeof state !== 'object' || state === null) return null;
  const index = (state as { readonly idx?: unknown }).idx;
  return typeof index === 'number' ? index : null;
}

/**
 * Which way a POP went. Forward through the stack is a push; anything else - back, or
 * a history entry that carries no index because it was not written by this router -
 * is a pop, because the back gesture is what a popstate overwhelmingly is and
 * guessing wrong there is the one direction a user would notice.
 */
export function popDirection(from: number | null, to: number | null): NavDirection {
  if (from === null || to === null) return 'pop';
  return to > from ? 'push' : 'pop';
}

/**
 * WHICH SCREEN THE USER WAS LOOKING AT when a popstate arrived - and this exists
 * because of a race that was measured rather than imagined.
 *
 * `popstate` fires with the destination already in `history.state`, so `to` is never
 * in doubt. The SOURCE is: whatever React last committed. The trap is that the
 * router's own popstate listener is registered before ours (it registers in
 * `BrowserRouter`'s layout effect; ours registers in a passive effect, which React
 * always runs later), and in Chromium the router's update had already COMMITTED by
 * the time our listener ran - so the ref holding "the index on screen" already held
 * the destination. Measured on the built bundle: a browser FORWARD produced
 * `from=1 to=1` and animated as a pop.
 *
 * Rather than depend on an ordering that is not ours to guarantee, the provider keeps
 * the last TWO committed indices and this function picks between them: if the
 * displayed one already IS the destination, the commit beat us and the source is the
 * one before it. Correct under either ordering, which is what makes it worth its four
 * lines.
 */
export function popSource(
  displayed: number | null,
  previouslyDisplayed: number | null,
  to: number | null,
): number | null {
  return displayed === to ? previouslyDisplayed : displayed;
}

/** The parts of a mouse event this decision depends on. */
export interface ClickIntent {
  readonly button: number;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly defaultPrevented: boolean;
}

/**
 * IS THIS CLICK OURS TO ANIMATE?
 *
 * Intercepting a link means calling `preventDefault`, and every one of these cases is
 * a click that must reach the browser instead: a middle click opens a tab, cmd or
 * ctrl opens a tab, shift opens a window, alt downloads, and a handler that already
 * called `preventDefault` has said the navigation is not happening at all. `target`
 * is the same rule in markup form.
 *
 * react-router's own `Link` makes these exact checks internally; interception happens
 * before that code runs, so it has to make them too, and it makes them here where a
 * test can see them.
 */
export function shouldInterceptClick(event: ClickIntent, target?: string | undefined): boolean {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (target !== undefined && target !== '' && target !== '_self') return false;
  return true;
}
